import React from 'react';
import { classNames } from '../../utils/class-names';

export type ButtonVariant =
  | 'purple'
  | 'white'
  | 'twitter'
  | 'light'
  | 'danger'
  | 'secondary'
  | 'primary'
  | 'success'
  | 'tool-button'
  | 'outline-purple';

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: 'sm' | 'lg';
  /** Render as a different component (e.g. Link); extra props pass through. */
  as?: React.ElementType;
  [key: string]: any;
}

const VARIANT_CLASSES: { [variant in ButtonVariant]: string } = {
  purple:
    'bg-secondary text-white border-transparent hover:bg-secondary-dark hover:text-white focus:ring-secondary',
  white: 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50 focus:ring-secondary',
  twitter:
    'bg-twitter text-white border-transparent hover:bg-twitter-dark hover:text-white focus:ring-twitter',
  light: 'bg-gray-100 text-gray-800 border-transparent hover:bg-gray-200 focus:ring-gray-300',
  danger:
    'bg-red-600 text-white border-transparent hover:bg-red-700 hover:text-white focus:ring-red-500',
  secondary:
    'bg-gray-500 text-white border-transparent hover:bg-gray-600 hover:text-white focus:ring-gray-400',
  primary:
    'bg-primary text-white border-transparent hover:bg-primary-dark hover:text-white focus:ring-primary',
  success:
    'bg-green-600 text-white border-transparent hover:bg-green-700 hover:text-white focus:ring-green-500',
  'tool-button':
    'bg-gray-400 text-white border-transparent font-bold text-xs hover:bg-gray-500 hover:text-white focus:ring-gray-400',
  'outline-purple':
    'bg-white text-secondary border-secondary hover:bg-secondary hover:text-white focus:ring-secondary',
};

const SIZE_CLASSES = {
  sm: 'px-2.5 py-1.5 text-xs',
  md: 'px-4 py-2 text-sm',
  lg: 'px-6 py-3 text-base',
};

/**
 * Tailwind replacement for the react-bootstrap Button. Full-width buttons
 * pass className="w-full flex" like any other utility.
 */
const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ variant = 'white', size, as: Component = 'button', className, children, ...rest }, ref) => {
    return (
      <Component
        ref={ref}
        type={Component === 'button' ? rest.type || 'button' : undefined}
        {...rest}
        className={classNames(
          'inline-flex items-center justify-center rounded-md border font-medium shadow-xs hover:no-underline focus:outline-hidden focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed',
          VARIANT_CLASSES[variant as ButtonVariant] || VARIANT_CLASSES.white,
          SIZE_CLASSES[(size as 'sm' | 'lg') || 'md'],
          className || ''
        )}
      >
        {children}
      </Component>
    );
  }
);

Button.displayName = 'Button';

export default Button;
